import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/CheckoutSuccess.css';
import { toast } from 'react-hot-toast';
import emailjs from '@emailjs/browser';

interface PaymentStatus {
  success: boolean;
  orderId: string;
  paymentStatus: string;
}

interface OrderData {
  name: string;
  email: string;
  addr: string;
  city: string;
  state: string;
  zipCode: string;
  total: number;
  items: Array<{
    n: string;
    q: number;
    p: number;
  }>;
}

const CheckoutSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const receiptRef = useRef<HTMLDivElement>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [receiptDate] = useState(new Date());
  const [orderData, setOrderData] = useState<OrderData | null>(null);

  useEffect(() => {
    emailjs.init("b6PfNSSHrsCGf_bdq");
  }, []);

  const formatDate = (date: Date): string =>
    date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const printReceipt = () => {
    if (!receiptRef.current) {
      toast.error("Receipt content is not available");
      return;
    }

    const printContent = receiptRef.current.innerHTML;
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>Sip'N'Security - Order Receipt #${paymentStatus?.orderId || 'N/A'}</title>
            <style>
              body { font-family: Arial, sans-serif; color: #333; line-height: 1.5; padding: 20px; max-width: 800px; margin: 0 auto; }
              .receipt-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #eee; padding-bottom: 20px; }
              .receipt-header h1 { color: #ff0000; margin: 0; }
              .receipt-header p { color: #666; margin: 5px 0; }
              .receipt-section { margin-bottom: 30px; }
              .receipt-section h2 { border-bottom: 1px solid #eee; padding-bottom: 10px; margin-bottom: 15px; color: #333; }
              .customer-info { display: flex; justify-content: space-between; flex-wrap: wrap; }
              .customer-info div { flex: 0 0 48%; margin-bottom: 20px; }
              .receipt-item { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
              .receipt-item:last-child { border-bottom: none; }
              .receipt-total { display: flex; justify-content: space-between; font-weight: bold; padding: 15px 0; margin-top: 15px; border-top: 2px solid #eee; }
              .thank-you { text-align: center; margin-top: 40px; color: #666; }
              @media print { body { font-size: 12pt; } }
            </style>
          </head>
          <body>
            ${printContent}
            <div class="thank-you">
              <p>Thank you for your purchase!</p>
              <p>Sip'N'Security</p>
            </div>
            <script>window.onload = () => { window.print(); window.onfocus = () => window.close(); }</script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const saveReceipt = () => {
    if (!receiptRef.current) {
      toast.error("Receipt content is not available");
      return;
    }

    const receiptHTML = receiptRef.current.innerHTML;
    const blob = new Blob(
      [
        `<html>
          <head>
            <title>Sip'N'Security - Order Receipt #${paymentStatus?.orderId || 'N/A'}</title>
            <style>
              body { font-family: Arial, sans-serif; color: #333; line-height: 1.5; padding: 20px; }
              .receipt-header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #eee; padding-bottom: 20px; }
              .receipt-header h1 { color: #ff0000; margin: 0; }
              .receipt-section { margin-bottom: 30px; }
              .receipt-section h2 { border-bottom: 1px solid #eee; padding-bottom: 10px; }
              .receipt-item { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #eee; }
              .receipt-total { display: flex; justify-content: space-between; font-weight: bold; padding: 15px 0; margin-top: 15px; border-top: 2px solid #eee; }
            </style>
          </head>
          <body>
            ${receiptHTML}
            <div style="text-align: center; margin-top: 40px; color: #666;">
              <p>Thank you for your purchase!</p>
              <p>Sip'N'Security</p>
            </div>
          </body>
        </html>`
      ],
      { type: "text/html" }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Vipers-Receipt-${paymentStatus?.orderId || 'N/A'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const sendReceiptEmail = async () => {
    if (emailSent) {
      toast("Receipt email has already been sent.", { icon: "ℹ️" });
      return;
    }
    if (!receiptRef.current || !orderData) {
      toast.error("Receipt details are not available.");
      return;
    }

    const templateParams = {
      to_email: orderData.email,
      to_name: orderData.name,
      order_number: paymentStatus?.orderId || 'N/A',
      order_date: formatDate(receiptDate),
      order_total: orderData.total.toFixed(2),
      transaction_id: paymentStatus?.orderId || "Not available",
      receipt_details: receiptRef.current.innerHTML,
    };

    try {
      await emailjs.send("service_l09crg6", "template_qr06vxy", templateParams);
      toast.success("Receipt email sent successfully!");
      setEmailSent(true);
    } catch (error) {
      console.error("Failed to send receipt email:", error);
      toast.error("Failed to send receipt email.");
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderDataParam = searchParams.get('orderData');
    
    if (orderDataParam) {
      try {
        const decodedData = decodeURIComponent(orderDataParam);
        const parsedData = JSON.parse(decodedData);
        setOrderData(parsedData);
        console.log("----------------------------------orderData----------------------------------", parsedData);
      } catch (err) {
        console.error('Error parsing order data:', err);
        toast.error('Error loading order details');
      }
    }
  }, [location]);

  useEffect(() => {
    const verifyPayment = async () => {
      const searchParams = new URLSearchParams(location.search);
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        setError('No session ID found in URL');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('https://us-central1-sipnsecurity.cloudfunctions.net/verifyPayment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to verify payment');
        }

        const data = await response.json();
        setPaymentStatus(data);
        toast.success('Payment verified successfully!');
      } catch (err: any) {
        console.error('Payment verification error:', err);
        setError(err.message || 'An error occurred while verifying your payment');
        toast.error('Failed to verify payment');
      } finally {
        setIsLoading(false);
      }
    };

    verifyPayment();
  }, [location]);

  if (isLoading) {
    return (
      <div className="checkout-success-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Verifying payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-success-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Payment Verification Failed</h2>
          <p>{error}</p>
          <div className="button-group">
            <button className='continue-button' onClick={() => navigate('/')}>Return Home</button>
            <button className='retry-button' onClick={() => navigate('/checkout')}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success-container">
      <div className="success-state">
        <div className="success-icon">✓</div>
        <h2>Payment Successful!</h2>
        <p>Your order has been processed successfully.</p>
        {paymentStatus && (
          <>
            <p className="order-number">
              Order Number: <span className="monospace">{paymentStatus.orderId}</span>
            </p>
            <p className="payment-status">
              Payment Status: <span className="monospace">{paymentStatus.paymentStatus}</span>
            </p>
          </>
        )}
        <div className="order-receipt" ref={receiptRef}>
          <div className="receipt-header">
            <h1>Sip'N'Security Order Receipt</h1>
            <p>Order #: {paymentStatus?.orderId}</p>
            <p>Date: {formatDate(receiptDate)}</p>
          </div>
          <div className="receipt-section">
            <h2>Customer Information</h2>
            <div className="customer-info">
              <div>
                <p><strong>Name:</strong> {orderData?.name}</p>
                <p><strong>Email:</strong> {orderData?.email}</p>
              </div>
              <div>
                <p><strong>Address:</strong> {orderData?.addr}</p>
                <p><strong>City/State/ZIP:</strong> {orderData?.city}, {orderData?.state} {orderData?.zipCode}</p>
              </div>
            </div>
          </div>
          <div className="receipt-section">
            <h2>Order Summary</h2>
            <div className="order-items">
              {orderData?.items.map((item, index) => (
                <div key={index} className="receipt-item">
                  <span>
                    {item.q} × {item.n}
                  </span>
                  <span>${(item.p * item.q).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="receipt-total">
              <span>Total:</span>
              <span>${orderData?.total.toFixed(2)}</span>
            </div>
          </div>
          <div className="receipt-section">
            <h2>Payment Information</h2>
            <p><strong>Payment Method:</strong> Credit Card</p>
            <p><strong>Transaction ID:</strong> {paymentStatus?.orderId}</p>
            <p><strong>Status:</strong> Paid</p>
          </div>
        </div>
        <div className="receipt-actions">
          <button className='continue-button' onClick={printReceipt}>
            <span className="icon">🖨️</span> Print Receipt
          </button>
          <button className='continue-button' onClick={saveReceipt}>
            <span className="icon">💾</span> Save Receipt
          </button>
          <button className='continue-button' onClick={sendReceiptEmail}>
            <span className="icon">📧</span> Email Receipt
          </button>
        </div>
        <div className="button-group">
          <button className='continue-button' onClick={() => navigate('/merchandise')}>Continue Shopping</button>
          <button className='continue-button' onClick={() => navigate('/')}>Go to Home</button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess; 